import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { VersionDisplay } from '@/components/ui/version-display';
import { useAuth } from '@/contexts/AuthContext';
import { authApi } from '@/lib/api';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

export function RegisterForm() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    masterKey: '',
    roles: [] as string[]
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await authApi.register(formData);
      login(response.token);
      toast({
        title: 'Success',
        description: 'Account created successfully',
      });
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create account',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden'>
      {/* Lightweight dotted background pattern */}
      <div className="absolute inset-0 opacity-[0.1] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='35' height='35' viewBox='0 0 35 35' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Ccircle cx='17.5' cy='17.5' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      {/* Simplified background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-60 h-60 bg-blue-400 rounded-full mix-blend-multiply filter blur-lg opacity-15 animate-float" />
        <div className="absolute -bottom-40 -left-40 w-60 h-60 bg-purple-400 rounded-full mix-blend-multiply filter blur-lg opacity-15 animate-float" style={{ animationDelay: '3s' }} />
      </div>

      {/* Header */}
      <div className='w-full fixed top-0 left-0 right-0 glass-strong border-b border-white/20 z-50 backdrop-blur-sm'>
        <div className='max-w-[1400px] mx-auto px-4 md:px-6'>
          <div className='flex justify-center items-center h-16'>
            <div className='flex items-center gap-3'>
              <div className="relative">
                <img src='/android-chrome-512x512.png' alt='Templar Screens Logo' className='w-8 h-8 drop-shadow-sm' />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse" />
              </div>
              <h1 className='text-xl font-bold text-slate-800 tracking-tight'>Templar Screens</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Register Form */}
      <Card className='w-[400px] mt-20 shadow-elegant border-0 bg-white/80 animate-scale-in'>
        <form onSubmit={handleSubmit}>
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-4 shadow-lg p-2">
              <img src='/android-chrome-512x512.png' alt='Templar Screens' className='w-full h-full object-contain' />
            </div>
            <CardTitle className="text-2xl font-bold from-yellow-400 to-orange-500">Create Account</CardTitle>
            <CardDescription className="text-base">Join the Templar Screens production team</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className='grid w-full items-center gap-4'>
              <div className='flex flex-col space-y-2'>
                <Label htmlFor='email' className="text-sm font-medium text-slate-700">Email Address</Label>
                <Input
                  id='email'
                  type='email'
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  className="h-11 bg-white/50 border-slate-200 focus:border-orange-500 transition-all duration-200"
                  required
                />
              </div>
              <div className='flex flex-col space-y-2'>
                <Label htmlFor='password' className="text-sm font-medium text-slate-700">Password</Label>
                <Input
                  id='password'
                  type='password'
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  className="h-11 bg-white/50 border-slate-200 focus:border-orange-500 transition-all duration-200"
                  required
                />
              </div>
              <div className='flex flex-col space-y-2'>
                <Label htmlFor='masterKey' className="text-sm font-medium text-slate-700">Master Key</Label>
                <Input
                  id='masterKey'
                  type='password'
                  placeholder="Enter master key"
                  value={formData.masterKey}
                  onChange={(e) => setFormData((prev) => ({ ...prev, masterKey: e.target.value }))}
                  className="h-11 bg-white/50 border-slate-200 focus:border-orange-500 transition-all duration-200"
                  required
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className='flex flex-col gap-4'>
            <Button 
              className='w-full h-11 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200' 
              type='submit' 
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Creating account...
                </div>
              ) : (
                'Create Account'
              )}
            </Button>
            <div className='flex flex-col gap-2 text-center text-sm'>
              <div className="flex items-center justify-center gap-2 text-slate-600">
                <span>Already have an account?</span>
                <Link to='/login' className='text-orange-600 hover:text-orange-700 font-medium hover:underline transition-colors duration-200'>
                  Sign in here
                </Link>
              </div>
            </div>
          </CardFooter>
        </form>
      </Card>

      {/* Version Number */}
      <VersionDisplay />
    </div>
  );
}
